<template>
    <TransitionModal>
        <Fixed
            v-if="isOpen"
            data-qa="stone-modal"
            @close="close"
        >
            <ModalWithHeaderAndFooter
                :title-element-id
                :data-qa="dataQa"
                @close="close"
            >
                <template
                    v-if="$slots['title']"
                    #title
                >
                    <h3
                        :id="titleElementId"
                        class="stone-title"
                    >
                        <slot name="title" />
                    </h3>
                </template>

                <template #default>
                    <slot name="default" />
                    <div
                        v-if="
                            isAdded &&
                            (!ringBuilderRing || viewCompletedRingRouteInternal)
                        "
                        v-qa="'stone-added'"
                        class="stone-added-wrapper"
                    >
                        {{ t('diamond_search.stone_already_added') }}
                    </div>
                </template>

                <template #footer>
                    <div class="select-button-wrapper">
                        <ThButton
                            v-if="!isAdded"
                            v-qa="'select-this-stone'"
                            color="var(--blue)"
                            type="solid"
                            @click="selectStone"
                        >
                            {{ t('diamond_search.select_stone') }}
                        </ThButton>

                        <ThButton
                            v-else-if="chooseYourRing"
                            v-qa="'choose-your-ring'"
                            :to="chooseYourRing"
                            color="var(--blue)"
                            type="solid"
                        >
                            {{ t('diamond_search.choose_your_ring') }}
                        </ThButton>

                        <ThButton
                            v-else-if="viewCompletedRingRouteInternal"
                            v-qa="'view-completed-ring'"
                            :to="viewCompletedRingRouteInternal"
                            color="var(--blue)"
                            type="solid"
                        >
                            {{ t('diamond_search.view_completed_ring') }}
                        </ThButton>
                    </div>
                </template>
            </ModalWithHeaderAndFooter>
        </Fixed>
    </TransitionModal>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import ModalWithHeaderAndFooter from '@/components/layout/modal/ModalWithHeaderAndFooter.vue'
import Fixed from '@/components/layout/modal/Fixed.vue'
import TransitionModal from '@/components/layout/modal/TransitionModal.vue'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton.ts'
import { useRouter } from 'vue-router'
import { computed } from 'vue'
import { useRoutes } from '@/router/routes.ts'
import type { StoneShapeCode } from '@/graphql/api'
import { useRingBuilder } from '@/plugins/ring-builder.ts'

const routes = useRoutes()
const router = useRouter()

const { isAdded, isAddedRoute, shape } = defineProps<{
    isOpen: boolean
    isAdded: boolean
    isAddedRoute: ButtonRouteRelative | null | undefined
    dataQa?: string
    shape?: StoneShapeCode
}>()

const { ring: ringBuilderRing } = useRingBuilder()

const viewCompletedRingRouteInternal = computed(() => {
    if (!isAddedRoute) {
        return undefined
    }
    if (router.currentRoute.value.name === isAddedRoute.name) {
        return undefined
    }
    return isAddedRoute
})

const chooseYourRing = computed(() => {
    if (ringBuilderRing.value || !isAdded) {
        return undefined
    }

    const to = routes.engagementRings({
        shape,
    })

    if (router.currentRoute.value.name === to.name) {
        return undefined
    }
    return to
})

const emit = defineEmits<{
    close: []
    select: []
}>()

const close = () => {
    emit('close')
}

const { t } = useI18n()

const selectStone = () => {
    emit('select')
}

const titleElementId = 'non-sku-stone-modal-title-id'
</script>

<style lang="scss" scoped>
.select-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    @include min-width(1024) {
        width: 570px;
        margin: 0 auto;
    }
}

.stone-added-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0 0 0;

    &:after {
        content: $icon-checkmark;
        color: $blue;
        font-family: $icon-font;
        margin: 0 0 0 10px;
        font-size: 0.875rem;
    }
}

.stone-title {
    text-transform: capitalize;
    font-size: 1.5rem;
    line-height: 1.2em;
    font-family: $parcel-font-stack;
    font-weight: 400;
    color: $blue;
}
</style>
